import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { bankFilePaymentsAPI, SplitAndMapPayInsToInvoicesResponse } from 'redux/bankFilePaymentsAPI';
import { showSnackbar } from 'redux/snackbar';
import i18n from 'i18next';
import type { MerchantAdminState } from 'redux/store';
import { fetchBankFilePayment } from '../bankFilePaymentsGlobalState';

type IgnoreRemainingUnmappedAmountParams = {
  bankFilePaymentId: string;
  remainingUnmappedAmount: number;
};

export const ignoreRemainingUnmappedAmount = createAsyncThunk<
  SplitAndMapPayInsToInvoicesResponse,
  IgnoreRemainingUnmappedAmountParams,
  { state: MerchantAdminState }
>(
  'bankFilePayments/ignoreRemainingUnmappedAmountDialog/ignore',
  async ({ bankFilePaymentId, remainingUnmappedAmount }: IgnoreRemainingUnmappedAmountParams, { dispatch }) => {
    const result = await bankFilePaymentsAPI.bankFilePayments.ignoreRemainingUnmappedAmount({
      bankFilePaymentId,
      remainingUnmappedAmount,
    });
    dispatch(fetchBankFilePayment({ bankFilePaymentId }));
    dispatch(
      showSnackbar(i18n.t('bankFilePayments.actions.ignoreRemainingUnmappedAmount.success', { bankFilePaymentId }), {
        severity: 'success',
      }),
    );
    return result;
  },
);

type DialogState = {
  isFetching: boolean;
  success: boolean | null;
  error: any;
};

type SliceState = Record<string, DialogState | undefined>;

const ignoreRemainingUnmappedAmountDialogSlice = createSlice({
  name: 'bankFilePayments/actions/ignoreRemainingUnmappedAmountDialog',
  initialState: {} as SliceState,
  reducers: {
    reset: (state, { payload: { bankFilePaymentId } }: { payload: { bankFilePaymentId: string } }) => {
      state[bankFilePaymentId] = undefined;
    },
  },
  extraReducers: builder => {
    builder.addCase(ignoreRemainingUnmappedAmount.pending, (state, { meta: { arg } }) => {
      state[arg.bankFilePaymentId] = { isFetching: true, success: null, error: null };
    });
    builder.addCase(ignoreRemainingUnmappedAmount.fulfilled, (state, { meta: { arg } }) => {
      state[arg.bankFilePaymentId] = { isFetching: false, success: true, error: null };
    });
    builder.addCase(ignoreRemainingUnmappedAmount.rejected, (state, { meta: { arg }, error }) => {
      state[arg.bankFilePaymentId] = { isFetching: false, success: false, error };
    });
  },
});

export const {
  actions: { reset },
  reducer: ignoreRemainingUnmappedAmountDialogReducer,
} = ignoreRemainingUnmappedAmountDialogSlice;
